import { accountTypesData } from "@/mocks/homepageData";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import { brokers } from "@/data/brokers";

export default function AccountTypesSection() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal(0.1);

  return (
    <section ref={sectionRef} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(sectionVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7C3AED]/40 mb-6">
            <i className="ri-vip-crown-line text-[#7C3AED]"></i>
            <span className="text-sm text-[#F8FAFC]">Choose Your Account</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            Account Types{" "}
            <span className="text-gradient">Comparison</span>
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
            Four account types designed for every trader level, from beginners to professional stock investors.
          </p>
        </div>

        {/* Account Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {accountTypesData.map((account) => (
            <div
              key={account.name}
              className={`relative rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                account.highlighted
                  ? "bg-[#1F2937] border-2 border-[#7C3AED] shadow-xl shadow-[#7C3AED]/10"
                  : "bg-[#1F2937] border border-[#374151] hover:border-[#374151]/80"
              }`}
            >
              {/* Recommended badge */}
              {account.highlighted && (
                <div className="absolute top-0 left-0 right-0 bg-[#7C3AED] text-white text-xs font-semibold text-center py-2 uppercase tracking-wider">
                  Recommended Choice
                </div>
              )}

              <div className={`p-6 md:p-8 ${account.highlighted ? "pt-14" : ""}`}>
                {/* Badge */}
                <span
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-4"
                  style={{
                    backgroundColor: `${account.badgeColor}20`,
                    color: account.badgeColor,
                  }}
                >
                  {account.badge}
                </span>

                {/* Account Name */}
                <h3 className="text-xl md:text-2xl font-bold text-[#F8FAFC] mb-2">
                  {account.name}
                </h3>

                {/* Min Deposit */}
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-3xl font-bold text-[#F8FAFC]">{account.minDeposit}</span>
                  <span className="text-[#94A3B8] text-sm">min deposit</span>
                </div>

                {/* Divider */}
                <div className="border-t border-[#374151] my-5"></div>

                {/* Features */}
                <ul className="space-y-3">
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-[#94A3B8]">Spread</span>
                    <span className="text-[#F8FAFC] font-medium">{account.spread}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-[#94A3B8]">Commission</span>
                    <span className="text-[#F8FAFC] font-medium">{account.commission}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-[#94A3B8]">Leverage</span>
                    <span className="text-[#F8FAFC] font-medium">{account.leverage}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-[#94A3B8]">Lot Size</span>
                    <span className="text-[#F8FAFC] font-medium">{account.lotSize}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-[#94A3B8]">Instruments</span>
                    <span className="text-[#F8FAFC] font-medium">{account.instruments}</span>
                  </li>
                </ul>

                {/* CTA */}
                <a
                  href={brokers.xm.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-center py-3 rounded-lg font-semibold text-sm mt-6 transition-all ${
                    account.highlighted
                      ? "bg-[#7C3AED] hover:bg-[#8B5CF6] text-white hover:shadow-lg hover:shadow-[#7C3AED]/20"
                      : "bg-[#374151] hover:bg-[#4B5563] text-[#F8FAFC]"
                  }`}
                >
                  Open {account.name.split(" ")[0]} Account
                </a>

                {/* Official Note */}
                {account.highlightNote && (
                  <p className="text-xs text-[#64748B] mt-4 text-center leading-relaxed">
                    {account.highlightNote}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}